import type { App } from '../../../hooks/use-app';
import uploadAttachment from '../../../api/attachments-actions/upload-attachment';
import { getAttachmentInfo } from '../../../api/attachments-actions/attachments-info';
import type { MediaStatusResult } from '../media-type-config';

interface AttachmentUploadResult {
  access_key: string;
  name: string;
  file_size: number;
}

/**
 * Factory that produces an upload function conforming to the
 * `(file: File, progress?: (event: ProgressEvent) => void) => Promise` contract.
 *
 * `uploadAttachment` uses `fetch()` which does not expose upload progress events,
 * so the `progress` callback is accepted for interface compliance but ignored.
 * `MediaField` degrades gracefully — showing an indeterminate state when no
 * progress events arrive.
 *
 * The returned result includes `file_size` because `MetadataParser`
 * reads `access_key` and `file_size` to build the metadata object
 * passed to `onMediaAdd`. Without `file_size`, `metadata.size` would
 * be `undefined`, which can break downstream attachment validation.
 */
export function getAttachmentUploader(ctx: App) {
  return async (file: File, _progress?: (event: ProgressEvent) => void): Promise<AttachmentUploadResult> => {
    const id = await uploadAttachment(file, ctx.globals.config.attachments_url, ctx.api.token);

    return {
      access_key: id,
      name: file.name,
      file_size: file.size,
    };
  };
}

/**
 * Factory that produces a status-checker function conforming to the
 * `(mediaID: string) => Promise<AttachmentResult>` contract.
 *
 * `getAttachmentInfo` requires an `editing` boolean as its 4th arg to
 * distinguish "new attachment" from "failed" on a 401 response.
 * `editing` is closure-captured at factory creation time so callers only
 * need to provide `mediaID`.
 */
const COMPLETE_STATUSES = new Set(['complete', 'newattachment']);

export function getAttachmentStatusChecker(ctx: App, editing: boolean) {
  return async (mediaID: string): Promise<MediaStatusResult> => {
    const result = await getAttachmentInfo(mediaID, ctx.globals.config.attachments_url, ctx.api.token, editing);
    return { ok: COMPLETE_STATUSES.has(result.state?.status) };
  };
}
